import { ListItemIcon, styled } from '@mui/material';

import { useMemo } from 'react';

import { TPrototypeShortDto } from '@app-velocity/av-api-v2';
import {
  InProgressIcon,
  PrototypesIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from '@app-velocity/icons';

import { MultipleProgress } from '../MultipleProgress';

export type SingleChallengeStatusProps = {
  scenario: TPrototypeShortDto;
};

const IconContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  svg: {
    height: theme.spacing(5),
    width: theme.spacing(5),
  },
}));

const getIconColorByStatus = (
  status: 'info' | 'success' | 'error' | 'idle'
) => {
  switch (status) {
    case 'error':
      return 'var(--error-error-icon-primary-color)';
    case 'success':
      return 'var(--success-success-icon-primary-color)';
    case 'info':
      return 'var(--info-info-icon-secondary-color)';
    default:
      return 'var(--background-background-secondary-color)';
  }
};

const getIconBackgroundColorByStatus = (
  status: 'info' | 'success' | 'error' | 'idle'
) => {
  switch (status) {
    case 'error':
      return 'var(--error-error-background-color)';
    case 'success':
      return 'var(--success-success-background-color)';
    case 'info':
      return 'var(--info-info-background-color)';
    default:
      return 'var(--icon-icon-secondary-color)';
  }
};

const StyledScenarioIcon = styled(ListItemIcon)<{
  status: 'info' | 'success' | 'error' | 'idle';
}>(({ theme, status }) => ({
  height: theme.spacing(16),
  width: theme.spacing(12),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.spacing(1),
  background: getIconBackgroundColorByStatus(status),
  svg: {
    fill: getIconColorByStatus(status),
  },
}));

export const SingleChallengeStatus = ({
  scenario,
}: SingleChallengeStatusProps) => {
  const scenarioIconStatus = useMemo(() => {
    const { lastSimulationStatus, failedSimulations, successfulSimulations } =
      scenario;
    if (lastSimulationStatus === 'IN_PROGRESS') return 'info';

    const normalizedFailures = failedSimulations || 0;
    const normalizedSuccesses = successfulSimulations || 0;

    if (normalizedFailures + normalizedSuccesses >= 1) {
      const ratio =
        normalizedSuccesses / (normalizedFailures + normalizedSuccesses);
      if (ratio >= 0.7) return 'success';

      return 'error';
    }

    return 'idle';
  }, [scenario]);

  const scenarioIcon = useMemo(() => {
    const { lastSimulationStatus, failedSimulations, successfulSimulations } =
      scenario;
    if (lastSimulationStatus === 'IN_PROGRESS')
      return (
        <IconContainer>
          <InProgressIcon />
        </IconContainer>
      );

    if ((failedSimulations || 0) + (successfulSimulations || 0) > 1) {
      return (
        <MultipleProgress
          small
          fails={failedSimulations || 0}
          successes={successfulSimulations || 0}
        />
      );
    }

    if (failedSimulations === 1) {
      return (
        <IconContainer>
          <ThumbsDownIcon />
        </IconContainer>
      );
    }

    if (successfulSimulations === 1) {
      return (
        <IconContainer>
          <ThumbsUpIcon />
        </IconContainer>
      );
    }

    return (
      <IconContainer>
        <PrototypesIcon />
      </IconContainer>
    );
  }, [scenario]);

  return (
    <StyledScenarioIcon status={scenarioIconStatus}>
      {scenarioIcon}
    </StyledScenarioIcon>
  );
};
